/**
 * Admin Dashboard
 * 
 * Overview of platform stats and recent activity.
 * Uses the Turn Platform design system - dark glassmorphism.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { adminGlassStyles } from '../../../lib/design';
import {
  Users,
  BookOpen,
  Layers,
  Mic,
  TrendingUp,
  ArrowRight,
  BookText,
  AlertCircle,
  Palette,
  BarChart3,
  MessageSquareWarning,
  X,
  LineChart,
} from 'lucide-react';
import { LexiconPage } from '@/components/LexiconPage';
import type { GoogleSheetsConfig } from '@/types/turnColor';

interface DashboardStats {
  totalParents: number;
  totalChildren: number;
  totalStorybooks: number;
  totalFlashcards: number;
  totalRecordings: number;
  recentSessions: number;
  wordsInLexicon: number;
  recentErrors: number;
  escalatedConversations: number;
}

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalParents: 0,
    totalChildren: 0,
    totalStorybooks: 0,
    totalFlashcards: 0,
    totalRecordings: 0,
    recentSessions: 0,
    wordsInLexicon: 0,
    recentErrors: 0,
    escalatedConversations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [lexiconOpen, setLexiconOpen] = useState(false);

  const isSuperAdmin = userEmail === 'info@turnliteracy.com';

  useEffect(() => {
    loadStats();
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserEmail(session?.user?.email ?? null);
    });
  }, []);

  const loadStats = async () => {
    try {
      const [
        parentsResult,
        childrenResult,
        storybooksResult,
        flashcardsResult,
        recordingsResult,
        sessionsResult,
        lexiconResult,
        errorsResult,
        escalatedResult,
      ] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact' }).eq('is_parent', true),
        supabase.from('children').select('id', { count: 'exact' }),
        supabase.from('published_storybooks').select('id', { count: 'exact' }).eq('is_published', true),
        supabase.from('published_flashcard_books').select('id', { count: 'exact' }).eq('is_published', true),
        supabase.from('reading_recordings').select('id', { count: 'exact' }),
        supabase.from('conversation_sessions').select('id', { count: 'exact' })
          .gte('session_start', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
        supabase.from('turn_color_words').select('id', { count: 'exact' }),
        supabase.from('user_error_log').select('id', { count: 'exact' })
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
        supabase.from('crm_conversations').select('id', { count: 'exact', head: true }).eq('needs_human', true),
      ]);

      setStats({
        totalParents: parentsResult.count || 0,
        totalChildren: childrenResult.count || 0,
        totalStorybooks: storybooksResult.count || 0,
        totalFlashcards: flashcardsResult.count || 0,
        totalRecordings: recordingsResult.count || 0,
        recentSessions: sessionsResult.count || 0,
        wordsInLexicon: lexiconResult.count || 0,
        recentErrors: errorsResult.count || 0,
        escalatedConversations: escalatedResult.count || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apple system colors for stat cards
  const statCards = [
    { 
      label: 'Parents', 
      value: stats.totalParents, 
      icon: Users, 
      color: '#0A84FF', // Apple blue
      link: '/admin/users' 
    },
    { 
      label: 'Children', 
      value: stats.totalChildren, 
      icon: Users, 
      color: '#30D158', // Apple green
      link: '/admin/users' 
    },
    { 
      label: 'Storybooks', 
      value: stats.totalStorybooks, 
      icon: BookOpen, 
      color: '#BF5AF2', // Apple purple
      link: '/admin/portal' 
    },
    { 
      label: 'Flashcard Books', 
      value: stats.totalFlashcards, 
      icon: Layers, 
      color: '#FF9F0A', // Apple orange
      link: '/admin/portal' 
    },
    { 
      label: 'Recordings', 
      value: stats.totalRecordings, 
      icon: Mic, 
      color: '#FF453A', // Apple red
      link: '/admin/analytics' 
    },
    { 
      label: 'Sessions (7d)', 
      value: stats.recentSessions, 
      icon: TrendingUp, 
      color: '#64D2FF', // Apple teal
      link: '/admin/analytics' 
    },
    { 
      label: 'Escalated', 
      value: stats.escalatedConversations, 
      icon: MessageSquareWarning, 
      color: '#FF453A', // Apple red
      link: '/admin/crm/inbox?filter=escalated',
      highlight: stats.escalatedConversations > 0,
    },
  ];

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-white/10 rounded-lg w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-white/10 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    {/* Full-screen Lexicon Modal */}
    {lexiconOpen && (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(10,10,12,0.97)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <h2 className="text-lg font-semibold text-white">Turn Color Lexicon</h2>
          <button
            onClick={() => setLexiconOpen(false)}
            className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <LexiconPage config={{} as GoogleSheetsConfig} onBack={() => setLexiconOpen(false)} />
        </div>
      </div>
    )}

    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-white/60 mt-1">Overview of your Turn Literacy platform</p>
      </div>

      {/* Superadmin tools — info@turnliteracy.com only */}
      {isSuperAdmin && (
        <div className="mb-8">
          <p className="text-xs font-medium text-white/30 uppercase tracking-wider mb-3">Admin Tools</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setLexiconOpen(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all hover:scale-[1.02] active:scale-100"
              style={{ background: 'rgba(48,209,88,0.12)', border: '1px solid rgba(48,209,88,0.25)', color: '#30D158' }}
            >
              <BookText className="w-4 h-4" />
              Edit Lexicon
            </button>
            <Link
              to="/admin/data-vis"
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all hover:scale-[1.02] active:scale-100"
              style={{ background: 'rgba(10,132,255,0.12)', border: '1px solid rgba(10,132,255,0.25)', color: '#0A84FF' }}
            >
              <LineChart className="w-4 h-4" />
              Data Vis
            </Link>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.link}>
              <div 
                className={`p-6 rounded-2xl transition-all hover:scale-[1.02] hover:brightness-110 ${
                  stat.highlight ? 'ring-1 ring-[#FF453A]/40' : ''
                }`}
                style={adminGlassStyles.card}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white/60">{stat.label}</p>
                    <p className="text-3xl font-bold text-white mt-2">{stat.value.toLocaleString()}</p>
                  </div>
                  <div className="relative">
                    <div 
                      className="p-3 rounded-xl"
                      style={{ 
                        background: `${stat.color}20`,
                        boxShadow: `0 4px 16px ${stat.color}30`
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: stat.color }} />
                    </div>
                    {stat.highlight && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#FF453A] rounded-full animate-pulse" />
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="p-6 rounded-2xl" style={adminGlassStyles.card}>
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="space-y-2">
            {[
              { label: 'Turn Color Renderer', path: '/admin/tools', Icon: Palette, color: '#BF5AF2' },
              { label: 'Manage Storybooks', path: '/admin/portal', Icon: BookOpen, color: '#0A84FF' },
              { label: 'Edit Lexicon', path: '/admin/tools', Icon: BookText, color: '#30D158' },
              { label: 'View Analytics', path: '/admin/analytics', Icon: BarChart3, color: '#64D2FF' },
            ].map((action) => (
              <Link
                key={action.path + action.label}
                to={action.path}
                className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <span className="flex items-center gap-3">
                  <action.Icon className="w-5 h-5" style={{ color: action.color }} />
                  <span className="font-medium text-white/80">{action.label}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#0A84FF] transition-colors" />
              </Link>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="p-6 rounded-2xl" style={adminGlassStyles.card}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">System Health</h2>
            <Link to="/admin/operations" className="text-sm text-[#0A84FF] hover:brightness-110">
              View All
            </Link>
          </div>
          
          {/* Lexicon */}
          <div className="flex items-center justify-between p-4 bg-[#5E5CE6]/10 rounded-xl mb-3 border border-[#5E5CE6]/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#5E5CE6]/20 rounded-lg">
                <BookText className="w-5 h-5 text-[#5E5CE6]" />
              </div>
              <div>
                <p className="font-medium text-white">Lexicon</p>
                <p className="text-sm text-white/60">{stats.wordsInLexicon.toLocaleString()} words</p>
              </div>
            </div>
            <span className="text-xs px-2 py-1 bg-[#30D158]/20 text-[#30D158] rounded-full font-medium border border-[#30D158]/30">
              Healthy
            </span>
          </div>

          {/* Errors (last 24h) */}
          <div className={`flex items-center justify-between p-4 rounded-xl border ${
            stats.recentErrors > 0 
              ? 'bg-[#FF453A]/10 border-[#FF453A]/20' 
              : 'bg-[#30D158]/10 border-[#30D158]/20'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats.recentErrors > 0 ? 'bg-[#FF453A]/20' : 'bg-[#30D158]/20'}`}>
                <AlertCircle className={`w-5 h-5 ${stats.recentErrors > 0 ? 'text-[#FF453A]' : 'text-[#30D158]'}`} />
              </div>
              <div>
                <p className="font-medium text-white">Errors (24h)</p>
                <p className="text-sm text-white/60">{stats.recentErrors} reported</p>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-medium border ${
              stats.recentErrors > 0 
                ? 'bg-[#FF453A]/20 text-[#FF453A] border-[#FF453A]/30' 
                : 'bg-[#30D158]/20 text-[#30D158] border-[#30D158]/30'
            }`}>
              {stats.recentErrors > 0 ? 'Attention' : 'Clear'}
            </span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminDashboard;
